import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Box, Paper, Alert, AlertTitle } from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import PageContainer from "../../common_components/PageContainer";
import PagesCommonHeader from "../../common_components/PagesCommonHeader";
import LastUpdatedInfo from "../../common_components/LastUpdatedInfo";
import CustomDatePicker from "../../common_components/daterangepicker/CustomDatePicker";
import CommonLoader from "../../common_components/CommonLoader";
import { Dropdown, ExportButton } from "../../common_components/FormInputs";
import AnalyticsLineChart from "../../common_components/AnalyticsLineChart/AnalyticsLineChart";
import DivergenceTable from "./DivergenceTable";
import ChangeLogsAccordions from "./ChangeLogsAccordions";
import ClientGroupHeader from "../../common_components/ClientGroupHeader";
import exportDivergenceExcel from "./exportDivergenceExcel";
import { DEFAULT_COLORS, METRIC_COLORS } from "../../theme/theme";
import moment from "moment";
import { CalendarDate } from "@internationalized/date";
import { fetchDivergenceReport, fetchChartData, fetchPreferences, selectReportData, selectReportLoading, selectChartData, selectChartLoading, selectAvailableMetrics, clearChartData, selectChartError, selectPreferences, fetchDivergenceChangeLogs, selectChangeLogs, selectChangeLogsLoading } from '../../redux/divergenceSlice';
// ✅ Import Custom Hooks
import { useClientSelection, useDateRange, useRefresh } from '../../hooks';
import { useAccountOverview } from '../../context/AccountOverviewContext';
import { clearPermissionDenied } from '../../redux/userDataSlice';
// ✅ Helper functions
const getMetricColors = (metricKey, index = 0) => {
    if (METRIC_COLORS[metricKey]) {
        return METRIC_COLORS[metricKey];
    }
    return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
};
const DIMENSION_OPTIONS = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' }
];
const DivergenceReportPage = () => {
    const dispatch = useDispatch();
    // Archive mode (Active/Archive toggle in the header) → load inactive (archived) clients.
    const { isArchiveMode } = useAccountOverview();
    // ✅ Custom Hooks
    const { selectedClientId, selectedGroupAccountId, selectedId, selectedClient, clientOptions, groupAccountOptions, currentSelectedGroupAccountLabel, utilityLoading, handleClientChange: baseClientChange, handleGroupChange: baseGroupChange } = useClientSelection('divergence_report', { status: isArchiveMode ? 'inactive' : 'active' });
    const { dateRange, compareDateRange, isCompareOn, dateRangeKey, compareDateRangeKey, handleDateChange: handleDateChangeBase } = useDateRange({
        defaultComparison: true,
        defaultCompareType: 'yoy',
    });
    const handleDateChange = (data) => {
        handleDateChangeBase(data);
    };
    // ✅ Local State
    const [primaryMetric, setPrimaryMetric] = useState('');
    const [secondaryMetric, setSecondaryMetric] = useState('');
    const [dimension, setDimension] = useState('month');
    const [showGACompare, setShowGACompare] = useState(false);
    // ✅ Refs
    const isInitialMetricSet = useRef(false);
    const previousClientId = useRef('');
    // ✅ Report Selectors
    const reportData = useSelector(selectReportData);
    const reportLoading = useSelector(selectReportLoading);
    const chartData = useSelector(selectChartData);
    const chartLoading = useSelector(selectChartLoading);
    const chartError = useSelector(selectChartError);
    const availableMetrics = useSelector(selectAvailableMetrics);
    const preferences = useSelector(selectPreferences);
    const changeLogs = useSelector(selectChangeLogs);
    const changeLogsLoading = useSelector(selectChangeLogsLoading);
    const hasAppliedRedisCache = useRef(false);
    useEffect(() => {
        if (reportData?.cache && !hasAppliedRedisCache.current) {
            hasAppliedRedisCache.current = true;
            const cache = reportData.cache;
            let mainRange = null;
            let compareRange = null;
            if (cache.startDate && cache.endDate) {
                const start = moment(cache.startDate);
                const end = moment(cache.endDate);
                if (start.isValid() && end.isValid()) {
                    mainRange = {
                        start: new CalendarDate(start.year(), start.month() + 1, start.date()),
                        end: new CalendarDate(end.year(), end.month() + 1, end.date())
                    };
                }
            }
            if (cache.comparison && cache.compareStartDate && cache.compareEndDate) {
                const start = moment(cache.compareStartDate);
                const end = moment(cache.compareEndDate);
                if (start.isValid() && end.isValid()) {
                    compareRange = {
                        start: new CalendarDate(start.year(), start.month() + 1, start.date()),
                        end: new CalendarDate(end.year(), end.month() + 1, end.date())
                    };
                }
            }
            else if (cache.comparison === false) {
                compareRange = null;
            }
            if (mainRange) {
                handleDateChange({ mainRange, compareRange });
            }
        }
    }, [reportData?.cache]);
    // ✅ Fetch report data function
    const fetchReportDataFn = useCallback(async () => {
        if (!selectedClientId || !dateRange?.start || !dateRange?.end)
            return;
        // Fetch report
        dispatch(fetchDivergenceReport({
            clientId: selectedId,
            childId: selectedGroupAccountId,
            parentId: selectedClientId,
            startDate: dateRange.start.toString(),
            endDate: dateRange.end.toString(),
            comparison: isCompareOn,
            ...(isCompareOn && compareDateRange?.start && compareDateRange?.end && {
                compareStartDate: compareDateRange.start.toString(),
                compareEndDate: compareDateRange.end.toString()
            })
        }));
    }, [dispatch, selectedClientId, selectedId, selectedGroupAccountId, dateRangeKey, compareDateRangeKey, isCompareOn]);
    // ✅ Fetch chart data function
    const fetchChartDataFn = useCallback(async () => {
        if (!selectedClientId || !dateRange?.start || !dateRange?.end || !primaryMetric)
            return;
        dispatch(fetchChartData({
            clientId: selectedId,
            startDate: dateRange.start.toString(),
            endDate: dateRange.end.toString(),
            metrics: primaryMetric,
            comparison_metrics: secondaryMetric || undefined,
            dimension,
            comparison: isCompareOn,
            ...(isCompareOn && compareDateRange?.start && compareDateRange?.end && {
                compareStartDate: compareDateRange.start.toString(),
                compareEndDate: compareDateRange.end.toString()
            })
        }));
    }, [dispatch, selectedClientId, selectedId, dateRangeKey, compareDateRangeKey, isCompareOn, primaryMetric, secondaryMetric, dimension]);
    const fetchAllDataFn = useCallback(async () => {
        await Promise.all([fetchReportDataFn(), fetchChartDataFn()]);
    }, [fetchReportDataFn, fetchChartDataFn]);
    // ✅ Refresh Hook
    const { isRefreshing, handleRefresh: triggerRefreshHandler } = useRefresh({
        moduleName: 'Divergence Report',
        onSuccess: fetchAllDataFn
    });
    // ✅ Fetch Preferences once on mount
    useEffect(() => {
        dispatch(fetchPreferences());
    }, [dispatch]);
    // Defensive: clear any stale sticky access-denied state on mount so a transient 403
    // from elsewhere doesn't leave this page permanently stuck on the access-restricted screen.
    useEffect(() => {
        dispatch(clearPermissionDenied());
    }, [dispatch]);
    // ✅ Auto dimension based on date range
    useEffect(() => {
        if (dateRange?.start && dateRange?.end) {
            const start = moment(dateRange.start.toString(), "YYYY-MM-DD");
            const end = moment(dateRange.end.toString(), "YYYY-MM-DD");
            const diffDays = end.diff(start, 'days') + 1;
            if (diffDays <= 14) {
                setDimension('day');
            }
            else if (diffDays <= 90) {
                setDimension('week');
            }
            else {
                setDimension('month');
            }
        }
    }, [dateRange]);
    // ✅ Custom handlers
    const handleClientChange = (val) => {
        isInitialMetricSet.current = false;
        baseClientChange(val);
    };
    const handleGroupChange = (val) => {
        baseGroupChange(val);
    };
    // ✅ Unified Fetch: Fetch report when dependencies change
    useEffect(() => {
        if (utilityLoading)
            return;
        // Wait until selectedClientId has been validated against the loaded (scope-filtered)
        // client list — otherwise this can fire once with a stale/out-of-scope id inherited
        // from AccountOverviewContext before useClientSelection's derive-effect corrects it,
        // tripping the backend's client-access 403 on the very first visit.
        if (!selectedClientId || !selectedClient)
            return;
        // Update local ref to track client changes
        if (previousClientId.current !== selectedClientId) {
            setPrimaryMetric('');
            setSecondaryMetric('');
            isInitialMetricSet.current = false;
            previousClientId.current = selectedClientId;
            dispatch(clearChartData());
        }
        fetchReportDataFn();
        dispatch(fetchDivergenceChangeLogs({ clientId: selectedId }));
    }, [utilityLoading, selectedClientId, selectedClient, selectedId, selectedGroupAccountId, fetchReportDataFn, dispatch]);
    // ✅ Fetch Chart Data
    useEffect(() => {
        if (utilityLoading)
            return;
        if (!selectedClientId || !selectedClient)
            return;
        fetchChartDataFn();
    }, [utilityLoading, selectedClientId, selectedClient, fetchChartDataFn]);
    // ✅ Auto-select metrics
    useEffect(() => {
        if (availableMetrics.length > 0 && !isInitialMetricSet.current) {
            const grossSales = availableMetrics.find(m => m.value === "shopify_gross_sales");
            const netSales = availableMetrics.find(m => m.value === "shopify_revenue" || m.value === "shopify_net_sales");
            if (grossSales && netSales) {
                setPrimaryMetric(grossSales.value);
                setSecondaryMetric(netSales.value);
            }
            else {
                setPrimaryMetric(availableMetrics[0]?.value || '');
                setSecondaryMetric(availableMetrics.find(m => m.value !== availableMetrics[0]?.value)?.value || '');
            }
            isInitialMetricSet.current = true;
        }
    }, [availableMetrics]);
    // ✅ Removed redundant chart data fetcher (merged into main fetcher)
    // ✅ Metric options
    const primaryMetricOptions = useMemo(() => {
        return availableMetrics
            .filter(m => m.value !== secondaryMetric)
            .map(m => ({ value: m.value, label: m.label }));
    }, [availableMetrics, secondaryMetric]);
    const secondaryMetricOptions = useMemo(() => {
        return availableMetrics
            .filter(m => m.value !== primaryMetric)
            .map(m => ({ value: m.value, label: m.label }));
    }, [availableMetrics, primaryMetric]);
    // ✅ Metric handlers
    const handlePrimaryMetricChange = (val) => {
        const newValue = val;
        setPrimaryMetric(newValue);
        if (newValue === secondaryMetric)
            setSecondaryMetric('');
    };
    const handleSecondaryMetricChange = (val) => {
        const newValue = val;
        setSecondaryMetric(newValue);
        if (newValue === primaryMetric)
            setPrimaryMetric('');
    };
    const handleDimensionChange = (val) => {
        setDimension(val);
    };
    // ✅ Transform chart data
    const transformedChartData = useMemo(() => {
        if (!chartData)
            return null;
        const primaryColors = getMetricColors(primaryMetric, 0);
        const secondaryColors = getMetricColors(secondaryMetric, 1);
        const series = chartData.series.map((s, index) => {
            const colors = index === 0 ? primaryColors : secondaryColors;
            return {
                name: s.name,
                data: s.data,
                yAxisIndex: s.yAxisIndex || 0,
                color: colors.color,
                compareColor: colors.compareColor
            };
        });
        let comparison = undefined;
        if (chartData.comparison?.labels && chartData.comparison?.series) {
            const comparisonSeries = {};
            Object.keys(chartData.comparison.series).forEach((key, index) => {
                const seriesName = chartData.series[index]?.name;
                if (seriesName) {
                    comparisonSeries[seriesName] = chartData.comparison.series[key];
                }
                else {
                    comparisonSeries[key] = chartData.comparison.series[key];
                }
            });
            comparison = { labels: chartData.comparison.labels, series: comparisonSeries };
        }
        return { categories: chartData.categories, series, comparison };
    }, [chartData, primaryMetric, secondaryMetric]);
    // ✅ Refresh Handler
    const handleRefresh = async () => {
        if (!selectedId || !dateRange?.start || !dateRange?.end)
            return;
        const dateRanges = [{
                startDate: dateRange.start.toString(),
                endDate: dateRange.end.toString()
            }];
        // if (isCompareOn && compareDateRange?.start && compareDateRange?.end) {
        //     dateRanges.push({
        //         startDate: compareDateRange.start.toString(),
        //         endDate: compareDateRange.end.toString()
        //     });
        // }
        triggerRefreshHandler(selectedId, dateRanges);
    };
    // ✅ Loading State
    if (utilityLoading) {
        return (_jsx(Box, { className: "reusable-table-loading", sx: { p: 4, textAlign: "center" }, children: _jsx(CommonLoader, { className: 'table-loader' }) }));
    }
    return (_jsx(PageContainer, { children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '8px' }, children: [_jsx(ClientGroupHeader, { selectedClientId: selectedClientId, clientOptions: clientOptions, handleClientChange: handleClientChange, selectedGroupAccountId: selectedGroupAccountId, groupAccountOptions: groupAccountOptions, handleGroupChange: handleGroupChange, currentSelectedGroupAccountLabel: currentSelectedGroupAccountLabel, isRefreshing: isRefreshing }), _jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: reportData?.last_updated, onRefresh: handleRefresh, disabled: isRefreshing, moduleName: 'divergence_report' }), rightSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(CustomDatePicker, { value: dateRange, compareValue: compareDateRange, onApply: handleDateChange, comparison: true, placeholder: "Select Date Range", comparePlaceholder: "Compare Range", disabled: isRefreshing }), _jsx(ExportButton, { onClick: () => {
                                    exportDivergenceExcel(reportData?.results, {
                                        clientName: selectedClient?.name || 'Unknown Client',
                                        startDate: dateRange?.start?.toString(),
                                        endDate: dateRange?.end?.toString(),
                                        compareStartDate: compareDateRange?.start?.toString(),
                                        compareEndDate: compareDateRange?.end?.toString(),
                                        hasComparison: isCompareOn,
                                        showGACompare,
                                        preferences
                                    });
                                }, loading: isRefreshing, disabled: !reportData?.results || reportLoading })] }) }), (isRefreshing || chartLoading || reportLoading) && (_jsx(Box, { className: "reusable-table-loading", sx: { p: 4, textAlign: "center", minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(CommonLoader, { className: 'table-loader' }) })), !(isRefreshing || chartLoading || reportLoading) && (_jsxs(_Fragment, { children: [_jsx(ChangeLogsAccordions, { changeLogs: changeLogs || [], dateRange: dateRange }), _jsxs(Paper, { elevation: 0, sx: { p: 1, border: '1px solid #D0D5DD', borderRadius: '8px' }, children: [_jsxs(Box, { sx: { mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }, children: [_jsxs(Box, { className: "matrics-Dropdown", sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                borderRadius: '8px',
                                                border: '1px solid #D0D5DD',
                                                bgcolor: '#FFFFFF',
                                                '& .dropdown-trigger': { border: 'none', borderRadius: '0px', background: 'transparent', minHeight: '40px', boxShadow: 'none' },
                                                '& .dropdown-trigger:hover, & .dropdown-trigger.dropdown-open': { border: 'none', boxShadow: 'none' },
                                                '& .form-input-wrapper': { gap: 0 }
                                            }, children: [_jsx(Box, { sx: { minWidth: '200px' }, children: _jsx(Dropdown, { value: primaryMetric, options: primaryMetricOptions, onChange: handlePrimaryMetricChange, placeholder: "Select Primary Metric", searchable: true }) }), _jsx(Box, { sx: {
                                                        px: 2, py: '10px', bgcolor: '#F9FAFB', color: '#98A2B3',
                                                        fontWeight: 500, fontSize: '14px',
                                                        borderLeft: '1px solid #D0D5DD', borderRight: '1px solid #D0D5DD',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                    }, children: "Vs" }), _jsx(Box, { sx: { minWidth: '200px' }, children: _jsx(Dropdown, { value: secondaryMetric, options: secondaryMetricOptions, onChange: handleSecondaryMetricChange, placeholder: "Select Secondary Metric", searchable: true }) })] }), _jsx(Box, { sx: { minWidth: '120px' }, children: _jsx(Dropdown, { value: dimension, options: DIMENSION_OPTIONS, onChange: handleDimensionChange, placeholder: "Select" }) })] }), chartError && (_jsxs(Alert, { severity: "warning", sx: { mb: 2 }, children: [_jsx(AlertTitle, { children: "Chart Warning" }), chartError] })), transformedChartData && (_jsx(AnalyticsLineChart, { categories: transformedChartData.categories, series: transformedChartData.series, comparison: transformedChartData.comparison, showLegend: true, height: 450, comapretoggle: "bottom", legendVariant: "pills" })), !transformedChartData && !chartError && (_jsx(Box, { sx: {
                                        height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#6B7280', border: '1px dashed #D1D5DB', borderRadius: 2
                                    }, children: "Select metrics to view chart" }))] }), _jsx(Paper, { elevation: 0, children: _jsx(DivergenceTable, { reportData: reportData?.results, loading: reportLoading, showGACompare: showGACompare, setShowGACompare: setShowGACompare }) })] }))] }) }));
};
export default DivergenceReportPage;
